/**
 * PaymentLinksHub — unified admin console for generating payment links across
 * 4 UAE gateways (Stripe, Tamara, Ziina, Tabby) and routing each link to one
 * of the finance platforms.
 *
 *  Layout
 *  ──────
 *  ┌ Provider tiles (from DB registry)  — pick provider (shows readiness state)
 *  ├ "Create Link" panel                — form (amount, description, customer, platform)
 *  ├ Records table                      — every link created, with status & filters
 *  └ Admin: "Manage Registry" drawer    — edit provider metadata, limits, enable/disable
 *
 *  Backend endpoints:
 *   • /api/admin/payment-links       — create / list / delete payment links
 *   • /api/admin/payment-methods     — CRUD on the registry (protected)
 *   • /api/payment-methods           — public read-only list (only enabled)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Copy, ExternalLink, Loader2, RefreshCw, Trash2, X,
  CheckCircle2, XCircle, Clock, AlertTriangle, Settings, Save,
  Power, RotateCcw, ImageIcon,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { floorAED } from '../../utils/money';
import { FINANCE_PLATFORM_META as PLATFORM_META } from '../../constants/financePlatforms';
import { DirhamPrice } from '../../components/DirhamPrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:    { icon: Clock,         color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',  ar: 'قيد الانتظار', en: 'Pending' },
  paid:       { icon: CheckCircle2,  color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', ar: 'مدفوع',   en: 'Paid' },
  authorized: { icon: CheckCircle2,  color: 'text-blue-400 bg-blue-500/10 border-blue-500/30',    ar: 'موثّق',       en: 'Authorized' },
  failed:     { icon: XCircle,       color: 'text-red-400 bg-red-500/10 border-red-500/30',       ar: 'فشل',         en: 'Failed' },
  expired:    { icon: AlertTriangle, color: 'text-white/40 bg-white/5 border-white/10',           ar: 'منتهي',       en: 'Expired' },
  cancelled:  { icon: XCircle,       color: 'text-white/40 bg-white/5 border-white/10',           ar: 'ملغى',        en: 'Cancelled' },
  refunded:   { icon: RefreshCw,     color: 'text-purple-400 bg-purple-500/10 border-purple-500/30', ar: 'مستردّ',    en: 'Refunded' },
  created:    { icon: Clock,         color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',  ar: 'قيد الانتظار', en: 'Created' },
};

export default function PaymentLinksHub() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [methods, setMethods] = useState([]);   // full registry (with ready/env)
  const [links, setLinks] = useState([]);
  const [countsByPlatform, setCountsByPlatform] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);  // for create-link panel
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [showRegistry, setShowRegistry] = useState(false);

  const loadMethods = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/payment-methods`, { headers: getAuthHeaders() });
      setMethods(r.data.data || []);
    } catch (e) {
      console.error('methods', e);
    }
  }, []);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProvider !== 'all') params.provider = filterProvider;
      if (filterPlatform !== 'all') params.platform = filterPlatform;
      const r = await axios.get(`${API_URL}/api/admin/payment-links`, { headers: getAuthHeaders(), params });
      setLinks(r.data.data || []);
      setCountsByPlatform(r.data.counts_by_platform || {});
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل الروابط' : 'Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [filterProvider, filterPlatform, isAr]);

  useEffect(() => { loadMethods(); }, [loadMethods]);
  useEffect(() => { loadLinks(); }, [loadLinks]);

  // Only show enabled providers on the create tiles
  const enabledMethods = useMemo(
    () => (methods || []).filter(m => m.enabled).sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100)),
    [methods]
  );

  return (
    <div className="min-h-screen bg-[#030303] relative">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance" />

      {/* Breadcrumb */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية',      label_en: 'Finance',   path: '/dashboard/finance' },
            { label_ar: 'روابط الدفع',  label_en: 'Payment Links', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {t('روابط الدفع', 'Payment Links')}
            </h1>
            <p className="text-white/50 text-sm">
              {t('أنشئ روابط دفع لأي من المنصات الأربع، مربوطة بمنصتك المالية.',
                'Generate payment links across 4 gateways, routed to your platforms.')}
            </p>
          </div>
          <Button
            onClick={() => setShowRegistry(true)}
            className="bg-white/10 hover:bg-white/15 text-white border border-white/10"
            data-testid="payment-methods-registry-open"
          >
            <Settings className="w-4 h-4 me-2" />
            {t('إدارة طرق الدفع', 'Manage Registry')}
          </Button>
        </div>

        {/* Provider tiles (from DB registry) */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {enabledMethods.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white/[0.02] border border-white/10 rounded-2xl">
              <p className="text-white/50 text-sm">
                {t('لا توجد طرق دفع مفعّلة. افتح "إدارة طرق الدفع" لإضافتها.',
                   'No enabled providers. Open "Manage Registry" to add or enable one.')}
              </p>
            </div>
          ) : (
            enabledMethods.map((m) => (
              <ProviderTile
                key={m.id}
                method={m}
                onSelect={() => setSelectedMethod(m)}
                t={t}
                isAr={isAr}
              />
            ))
          )}
        </section>

        {/* Platform totals strip */}
        <section className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
          {Object.entries(PLATFORM_META).map(([id, m]) => {
            const c = countsByPlatform[id] || { count: 0, paid: 0, paid_amount: 0 };
            return (
              <div key={id}
                   className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                   data-testid={`payment-links-platform-card-${id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/80 text-sm font-medium">{isAr ? m.ar : m.en}</span>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-bold text-white"><DirhamPrice value={c.paid_amount} decimals={0} size="lg" bold /></div>
                    <div className="text-xs text-white/40 mt-1">
                      {c.paid}/{c.count} {t('مدفوع', 'paid')}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Filters + records */}
        <section className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-white/5 flex-wrap">
            <h3 className="text-white font-bold flex-1">{t('سجل الروابط', 'Link Records')}</h3>
            <select
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value)}
              className="h-9 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm"
              data-testid="payment-links-filter-provider"
            >
              <option value="all">{t('كل المزودين', 'All providers')}</option>
              {(methods || []).map(m => (
                <option key={m.id} value={m.provider}>{isAr ? m.display_name_ar : m.display_name}</option>
              ))}
            </select>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="h-9 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm"
              data-testid="payment-links-filter-platform"
            >
              <option value="all">{t('كل المنصات', 'All platforms')}</option>
              {Object.entries(PLATFORM_META).map(([id, m]) => (
                <option key={id} value={id}>{isAr ? m.ar : m.en}</option>
              ))}
            </select>
            <Button
              onClick={loadLinks}
              variant="outline"
              size="sm"
              className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
              data-testid="payment-links-refresh"
            >
              <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
              {t('تحديث', 'Refresh')}
            </Button>
          </div>

          <LinksTable links={links} methods={methods} loading={loading} t={t} isAr={isAr} onDelete={loadLinks} />
        </section>
      </main>

      {/* Create Link Slide-over */}
      <AnimatePresence>
        {selectedMethod && (
          <CreateLinkPanel
            method={selectedMethod}
            onClose={() => setSelectedMethod(null)}
            onCreated={() => { setSelectedMethod(null); loadLinks(); }}
            t={t}
            isAr={isAr}
          />
        )}
      </AnimatePresence>

      {/* Registry drawer */}
      <AnimatePresence>
        {showRegistry && (
          <RegistryDrawer
            methods={methods}
            onClose={() => setShowRegistry(false)}
            onChanged={loadMethods}
            t={t}
            isAr={isAr}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


/* ─── Provider tile (uses DB registry data) ─────────────────────── */
function ProviderTile({ method, onSelect, t, isAr }) {
  const ready = method.ready;
  const env = method.env;
  const name = isAr ? method.display_name_ar : method.display_name;
  const desc = isAr ? method.description_ar : method.description_en;
  return (
    <motion.button
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
      className={`relative bg-white/[0.02] border rounded-2xl p-5 text-start transition-all ${
        ready
          ? 'border-white/10 hover:border-[#d4af37]/40 cursor-pointer'
          : 'border-white/5 cursor-pointer opacity-95'
      }`}
      data-testid={`payment-links-provider-${method.provider}`}
    >
      <div className="absolute top-3 end-3">
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
          ready ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'
        }`}>
          {ready ? (env || 'ready') : t('يحتاج مفتاح', 'Setup')}
        </span>
      </div>
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-3 overflow-hidden bg-white/[0.02]"
        style={{ border: `1px solid ${method.color}30` }}
      >
        {method.logo_url ? (
          <img src={method.logo_url} alt={name} className="w-full h-full object-contain p-1" />
        ) : (
          <span className="text-lg font-bold" style={{ color: method.color }}>{name?.[0]}</span>
        )}
      </div>
      <h3 className="text-white font-bold text-lg">{name}</h3>
      {desc && <p className="text-white/50 text-xs mt-1 line-clamp-2">{desc}</p>}
      <div className="mt-4 flex items-center gap-1.5 text-xs text-[#d4af37] font-medium">
        <Plus className="w-3.5 h-3.5" />
        {t('إنشاء رابط', 'Create link')}
      </div>
    </motion.button>
  );
}


/* ─── Create Link panel ─────────────────────────────────────────── */
function CreateLinkPanel({ method, onClose, onCreated, t, isAr }) {
  const providerReady = method.ready;
  const name = isAr ? method.display_name_ar : method.display_name;
  const allowedPlatforms = method.platforms?.length > 0
    ? method.platforms
    : Object.keys(PLATFORM_META);

  const [form, setForm] = useState({
    amount: '',
    description: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    platform: allowedPlatforms[0] || 'cloudgm_marketing',
    reference: '',
  });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const amountNum = parseFloat(form.amount);
  const belowMin = method.min_amount != null && amountNum > 0 && amountNum < method.min_amount;
  const aboveMax = method.max_amount != null && amountNum > method.max_amount;
  const feePercent = method.fee_enabled ? Number(method.fee_percent || 0) : 0;
  const feeAmount = amountNum > 0 ? floorAED(amountNum * feePercent / 100) : 0;
  const finalAmount = amountNum > 0 ? floorAED(amountNum + feeAmount) : 0;

  const canSubmit =
    providerReady &&
    amountNum > 0 && !belowMin && !aboveMax &&
    form.description.trim().length > 0 &&
    form.customer_name.trim().length > 0 &&
    !!form.platform;

  const submit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    try {
      const body = {
        provider: method.provider,
        platform: form.platform,
        amount: amountNum,
        currency: method.currency || 'AED',
        description: form.description.trim(),
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        reference: form.reference.trim() || null,
      };
      const r = await axios.post(`${API_URL}/api/admin/payment-links`, body,
        { headers: getAuthHeaders() });
      setResult(r.data);
      toast.success(t('✓ تم إنشاء الرابط', '✓ Link created'));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(t('فشل الإنشاء', 'Create failed') + ': ' + msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
      />
      <motion.div
        initial={{ x: isAr ? '-100%' : '100%' }}
        animate={{ x: 0 }}
        exit={{ x: isAr ? '-100%' : '100%' }}
        transition={{ type: 'tween', duration: 0.25 }}
        className={`fixed top-0 ${isAr ? 'left-0' : 'right-0'} h-full w-full max-w-lg bg-[#0a0a0a] border-${isAr ? 'e' : 's'} border-white/10 z-50 overflow-y-auto`}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10 p-4 flex items-center gap-3">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-white/70" data-testid="create-link-close">
            <X className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
               style={{ border: `1px solid ${method.color}30` }}>
            {method.logo_url ? (
              <img src={method.logo_url} alt="" className="w-full h-full object-contain p-1" />
            ) : (
              <span style={{ color: method.color }} className="font-bold">{name?.[0]}</span>
            )}
          </div>
          <h2 className="text-white font-bold text-lg flex-1">
            {t('إنشاء رابط', 'Create')} — {name}
          </h2>
        </div>

        <div className="p-6 space-y-5">
          {!providerReady && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm">
              {t(`مزود ${name} غير مُعدّ. `, `${name} not configured. `)}
              {method.config_hint || t('أضف مفاتيح API في ملف backend/.env', 'Add API keys in backend/.env')}
            </div>
          )}

          {(method.min_amount != null || method.max_amount != null) && (
            <div className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs">
              {t('نطاق المبلغ المسموح', 'Allowed amount')}: {method.min_amount ?? '—'} — {method.max_amount ?? '∞'} {method.currency || 'AED'}
            </div>
          )}

          {result ? (
            <ResultView result={result} onDone={onCreated} t={t} />
          ) : (
            <>
              <Row label={t('المنصة المستفيدة', 'Beneficiary platform') + ' *'}>
                <select
                  value={form.platform}
                  onChange={(e) => setForm(f => ({ ...f, platform: e.target.value }))}
                  className="w-full h-11 rounded-md bg-[#050505] border border-white/10 text-white px-3"
                  data-testid="create-link-platform"
                >
                  {allowedPlatforms.map((id) => (
                    <option key={id} value={id}>{isAr ? PLATFORM_META[id]?.ar || id : PLATFORM_META[id]?.en || id}</option>
                  ))}
                </select>
              </Row>

              <div className="grid grid-cols-2 gap-3">
                <Row label={t('المبلغ', 'Amount') + ` (${method.currency || 'AED'}) *`}>
                  <Input
                    type="number" step="0.01" min="1"
                    value={form.amount}
                    onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="bg-[#050505] border-white/10 text-white"
                    data-testid="create-link-amount"
                  />
                  {(belowMin || aboveMax) && (
                    <div className="mt-1 text-[11px] text-red-400">
                      {belowMin && t(`أقل من الحد الأدنى (${method.min_amount})`, `Below minimum (${method.min_amount})`)}
                      {aboveMax && t(`أعلى من الحد الأقصى (${method.max_amount})`, `Above maximum (${method.max_amount})`)}
                    </div>
                  )}
                </Row>
                <Row label={t('مرجع (اختياري)', 'Reference (optional)')}>
                  <Input
                    value={form.reference}
                    onChange={(e) => setForm(f => ({ ...f, reference: e.target.value }))}
                    placeholder="INV-1024"
                    className="bg-[#050505] border-white/10 text-white"
                    data-testid="create-link-reference"
                  />
                </Row>
              </div>

              {/* Fee breakdown preview */}
              {amountNum > 0 && feePercent > 0 && (
                <div className="p-3 rounded-xl bg-[#d4af37]/5 border border-[#d4af37]/20"
                     data-testid="create-link-fee-breakdown">
                  <div className="text-[11px] text-[#d4af37] mb-2 font-medium">
                    {t(`عمولة ${isAr ? method.display_name_ar : method.display_name} (${feePercent}%) ستُضاف تلقائياً`,
                       `${method.display_name} commission (${feePercent}%) auto-added`)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-white/40">{t('المبلغ الأساسي', 'Base')}</div>
                      <div className="text-white font-mono">{floorAED(amountNum).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-white/40">{t('العمولة', 'Fee')}</div>
                      <div className="text-amber-400 font-mono">+{floorAED(feeAmount).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-white/40">{t('العميل يدفع', 'Customer pays')}</div>
                      <div className="text-emerald-400 font-bold font-mono">{floorAED(finalAmount).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}

              <Row label={t('الوصف', 'Description') + ' *'}>
                <Input
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder={t('خدمة تسجيل رخصة تجارية', 'Trade license service')}
                  className="bg-[#050505] border-white/10 text-white"
                  data-testid="create-link-description"
                />
              </Row>

              <Row label={t('اسم العميل', 'Customer name') + ' *'}>
                <Input
                  value={form.customer_name}
                  onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  className="bg-[#050505] border-white/10 text-white"
                  data-testid="create-link-customer-name"
                />
              </Row>

              <div className="grid grid-cols-2 gap-3">
                <Row label={t('بريد العميل', 'Customer email')}>
                  <Input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))}
                    className="bg-[#050505] border-white/10 text-white"
                    dir="ltr"
                    data-testid="create-link-customer-email"
                  />
                </Row>
                <Row label={t('هاتف العميل', 'Customer phone')}>
                  <Input
                    value={form.customer_phone}
                    onChange={(e) => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                    placeholder="+971 50 123 4567"
                    className="bg-[#050505] border-white/10 text-white"
                    dir="ltr"
                    data-testid="create-link-customer-phone"
                  />
                </Row>
              </div>

              <Button
                onClick={submit}
                disabled={!canSubmit || creating}
                className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-11"
                data-testid="create-link-submit"
              >
                {creating
                  ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الإنشاء...', 'Creating...')}</>
                  : <><Plus className="w-4 h-4 me-2" />{t('إنشاء الرابط', 'Create link')}</>}
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}


function Row({ label, children }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">{label}</label>
      {children}
    </div>
  );
}


function ResultView({ result, onDone, t }) {
  const copyLink = () => {
    navigator.clipboard.writeText(result.checkout_url).then(() => {
      toast.success(t('تم النسخ', 'Copied'));
    });
  };
  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span className="text-emerald-300 font-bold">{t('تم إنشاء الرابط بنجاح', 'Link created successfully')}</span>
        </div>
        <div className="text-emerald-200/80 text-xs">
          {t('انسخه أو شاركه مع العميل، وسيصل السجل تلقائياً إلى ',
             'Copy or share with the customer. The record will show up under ')}
          <b>{PLATFORM_META[result.platform]?.ar || result.platform}</b>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <p className="text-xs text-white/50 mb-2">{t('رابط الدفع', 'Payment link')}</p>
        <p className="text-white text-xs break-all font-mono" dir="ltr">{result.checkout_url}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button onClick={copyLink} className="bg-white/10 hover:bg-white/15 text-white" data-testid="result-copy-link">
          <Copy className="w-4 h-4 me-1.5" />
          {t('نسخ', 'Copy')}
        </Button>
        <a href={result.checkout_url} target="_blank" rel="noreferrer">
          <Button className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black w-full" data-testid="result-open-link">
            <ExternalLink className="w-4 h-4 me-1.5" />
            {t('فتح', 'Open')}
          </Button>
        </a>
      </div>

      <button onClick={onDone}
              className="w-full text-white/60 text-xs hover:text-white/90 pt-2"
              data-testid="result-done">
        {t('حسناً — عودة إلى القائمة', 'Done — back to list')}
      </button>
    </div>
  );
}


/* ─── Registry drawer (admin) ───────────────────────────────────── */
function RegistryDrawer({ methods, onClose, onChanged, t, isAr }) {
  const [editing, setEditing] = useState(null); // method object being edited
  const [savingId, setSavingId] = useState(null);

  const toggleEnabled = async (m) => {
    setSavingId(m.id);
    try {
      await axios.post(`${API_URL}/api/admin/payment-methods/${m.id}/toggle`,
        {}, { headers: getAuthHeaders() });
      toast.success(t('تم تحديث الحالة', 'Status updated'));
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    } finally {
      setSavingId(null);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm(t(
      'استعادة الإعدادات الافتراضية؟ سيتم استبدال جميع طرق الدفع الحالية.',
      'Reset to defaults? This will replace all current entries.',
    ))) return;
    try {
      await axios.post(`${API_URL}/api/admin/payment-methods/reset`, {},
        { headers: getAuthHeaders() });
      toast.success(t('تمت الاستعادة', 'Reset done'));
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
      />
      <motion.div
        initial={{ x: isAr ? '-100%' : '100%' }}
        animate={{ x: 0 }}
        exit={{ x: isAr ? '-100%' : '100%' }}
        transition={{ type: 'tween', duration: 0.25 }}
        className={`fixed top-0 ${isAr ? 'left-0' : 'right-0'} h-full w-full max-w-2xl bg-[#0a0a0a] border-white/10 z-50 overflow-y-auto`}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10 p-4 flex items-center gap-3">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-white/70" data-testid="registry-close">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-white font-bold text-lg flex-1">{t('إدارة طرق الدفع', 'Payment Methods Registry')}</h2>
          <Button onClick={resetDefaults} variant="outline" size="sm"
                  className="border-white/10 bg-transparent text-amber-300 hover:bg-amber-500/10"
                  data-testid="registry-reset">
            <RotateCcw className="w-3.5 h-3.5 me-1" />
            {t('استعادة', 'Reset')}
          </Button>
        </div>

        <div className="p-6 space-y-3">
          <p className="text-xs text-white/50 mb-3">
            {t('تحكّم مركزي بالمزودين: تفعيل/تعطيل، تعديل الحدود، تغيير الشعار والوصف، والمنصات المسموح ربطها.',
               'Central control over providers: enable/disable, edit limits, change logo & description, and allowed platforms.')}
          </p>

          {(methods || []).map((m) => (
            <div key={m.id}
                 className={`rounded-2xl border ${m.enabled ? 'border-white/10' : 'border-white/5 opacity-70'} bg-white/[0.02] p-4`}
                 data-testid={`registry-method-${m.provider}`}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden bg-white/[0.02]"
                     style={{ border: `1px solid ${m.color}30` }}>
                  {m.logo_url ? (
                    <img src={m.logo_url} alt="" className="w-full h-full object-contain p-1" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-white/30" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-white font-bold">{isAr ? m.display_name_ar : m.display_name}</h4>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      m.ready ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'
                    }`}>
                      {m.ready ? m.env : t('يحتاج مفتاح', 'setup')}
                    </span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      m.enabled ? 'bg-blue-500/15 text-blue-300' : 'bg-white/5 text-white/40'
                    }`}>
                      {m.enabled ? t('مفعّل', 'enabled') : t('معطّل', 'disabled')}
                    </span>
                  </div>
                  <p className="text-white/50 text-xs mt-1">
                    {isAr ? m.description_ar : m.description_en}
                  </p>
                  <div className="text-[11px] text-white/40 mt-1">
                    {t('الحدود', 'Limits')}: {m.min_amount ?? '—'} — {m.max_amount ?? '∞'} {m.currency || 'AED'}
                    {' • '}
                    {t('عمولة', 'Fee')}: <span className={m.fee_enabled ? 'text-[#d4af37]' : 'text-white/30 line-through'}>{m.fee_percent ?? 0}%</span>
                    {' • '}
                    {m.platforms?.length || 0} {t('منصة', 'platforms')}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button onClick={() => toggleEnabled(m)}
                          disabled={savingId === m.id}
                          size="sm" variant="outline"
                          className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                          data-testid={`registry-toggle-${m.provider}`}>
                    <Power className="w-3.5 h-3.5 me-1" />
                    {m.enabled ? t('تعطيل', 'Disable') : t('تفعيل', 'Enable')}
                  </Button>
                  <Button onClick={() => setEditing(m)} size="sm"
                          className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black"
                          data-testid={`registry-edit-${m.provider}`}>
                    <Settings className="w-3.5 h-3.5 me-1" />
                    {t('تعديل', 'Edit')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {editing && (
        <EditMethodModal method={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }} t={t} isAr={isAr} />
      )}
    </>
  );
}


function EditMethodModal({ method, onClose, onSaved, t, isAr }) {
  const [form, setForm] = useState({
    display_name: method.display_name || '',
    display_name_ar: method.display_name_ar || '',
    description_en: method.description_en || '',
    description_ar: method.description_ar || '',
    logo_url: method.logo_url || '',
    color: method.color || '#d4af37',
    min_amount: method.min_amount ?? '',
    max_amount: method.max_amount ?? '',
    sort_order: method.sort_order ?? 100,
    platforms: method.platforms || [],
    config_hint: method.config_hint || '',
  });
  const [saving, setSaving] = useState(false);

  const togglePlatform = (id) => {
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(id)
        ? f.platforms.filter(p => p !== id)
        : [...f.platforms, id],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name,
        display_name_ar: form.display_name_ar,
        description_en: form.description_en,
        description_ar: form.description_ar,
        logo_url: form.logo_url,
        color: form.color,
        min_amount: form.min_amount === '' ? null : parseFloat(form.min_amount),
        max_amount: form.max_amount === '' ? null : parseFloat(form.max_amount),
        sort_order: parseInt(form.sort_order || 100, 10),
        platforms: form.platforms,
        config_hint: form.config_hint,
      };
      await axios.patch(`${API_URL}/api/admin/payment-methods/${method.id}`,
        payload, { headers: getAuthHeaders() });
      toast.success(t('تم الحفظ', 'Saved'));
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}
           dir={isAr ? 'rtl' : 'ltr'}
           data-testid="registry-edit-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg overflow-hidden" style={{ border: `1px solid ${form.color}30` }}>
            {form.logo_url ? <img src={form.logo_url} alt="" className="w-full h-full object-contain p-1" /> : <ImageIcon className="w-full h-full p-2 text-white/30" />}
          </div>
          <h3 className="text-white font-bold flex-1">{t('تعديل', 'Edit')} — {method.provider}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-white/60">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Row label={t('الاسم (EN)', 'Name (EN)')}>
              <Input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                className="bg-[#050505] border-white/10 text-white" data-testid="edit-name-en" />
            </Row>
            <Row label={t('الاسم (AR)', 'Name (AR)')}>
              <Input value={form.display_name_ar} onChange={e => setForm(f => ({ ...f, display_name_ar: e.target.value }))}
                className="bg-[#050505] border-white/10 text-white" data-testid="edit-name-ar" />
            </Row>
          </div>

          <Row label={t('الوصف (EN)', 'Description (EN)')}>
            <Input value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))}
              className="bg-[#050505] border-white/10 text-white" />
          </Row>
          <Row label={t('الوصف (AR)', 'Description (AR)')}>
            <Input value={form.description_ar} onChange={e => setForm(f => ({ ...f, description_ar: e.target.value }))}
              className="bg-[#050505] border-white/10 text-white" />
          </Row>

          <Row label={t('رابط الشعار', 'Logo URL')}>
            <Input value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
              className="bg-[#050505] border-white/10 text-white" dir="ltr" data-testid="edit-logo-url" />
          </Row>

          <div className="grid grid-cols-3 gap-3">
            <Row label={t('اللون', 'Color')}>
              <Input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="bg-[#050505] border-white/10 h-10 p-1" />
            </Row>
            <Row label={t('حد أدنى', 'Min')}>
              <Input type="number" step="0.01" value={form.min_amount} onChange={e => setForm(f => ({ ...f, min_amount: e.target.value }))}
                className="bg-[#050505] border-white/10 text-white" data-testid="edit-min" />
            </Row>
            <Row label={t('حد أقصى', 'Max')}>
              <Input type="number" step="0.01" value={form.max_amount} onChange={e => setForm(f => ({ ...f, max_amount: e.target.value }))}
                className="bg-[#050505] border-white/10 text-white" data-testid="edit-max" />
            </Row>
          </div>

          <Row label={t('ترتيب العرض', 'Sort order')}>
            <Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
              className="bg-[#050505] border-white/10 text-white" />
          </Row>

          <Row label={t('المنصات المسموح ربطها', 'Allowed platforms')}>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PLATFORM_META).map(([id, m]) => (
                <label key={id}
                       className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                         form.platforms.includes(id)
                           ? 'bg-[#d4af37]/10 border-[#d4af37]/40 text-white'
                           : 'bg-[#050505] border-white/10 text-white/60'
                       }`}
                       data-testid={`edit-platform-${id}`}>
                  <input type="checkbox"
                         checked={form.platforms.includes(id)}
                         onChange={() => togglePlatform(id)}
                         className="accent-[#d4af37]" />
                  {isAr ? m.ar : m.en}
                </label>
              ))}
            </div>
          </Row>

          <Row label={t('تلميح الإعداد', 'Config hint')}>
            <Input value={form.config_hint} onChange={e => setForm(f => ({ ...f, config_hint: e.target.value }))}
              className="bg-[#050505] border-white/10 text-white" />
          </Row>

          <div className="pt-3 flex items-center gap-2">
            <Button onClick={save} disabled={saving}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold flex-1"
              data-testid="edit-save">
              {saving
                ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الحفظ...', 'Saving...')}</>
                : <><Save className="w-4 h-4 me-2" />{t('حفظ التغييرات', 'Save changes')}</>}
            </Button>
            <Button onClick={onClose} variant="outline"
              className="border-white/10 bg-transparent text-white/80 hover:bg-white/5">
              {t('إلغاء', 'Cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── Links table ───────────────────────────────────────────────── */
function LinksTable({ links, methods, loading, t, isAr, onDelete }) {
  const providerMeta = useMemo(() => {
    const map = {};
    (methods || []).forEach(m => {
      map[m.provider] = m;
    });
    return map;
  }, [methods]);

  const deleteLink = async (id) => {
    if (!window.confirm(t('حذف الرابط؟', 'Delete this link?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/payment-links/${id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      onDelete();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const copyLink = (url) => navigator.clipboard.writeText(url).then(() => toast.success(t('تم النسخ', 'Copied')));

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }
  if (links.length === 0) {
    return (
      <div className="p-12 text-center text-white/40 text-sm">
        {t('لا توجد روابط بعد. اختر مزود دفع لبدء إنشاء أول رابط.',
          'No links yet. Pick a provider above to create your first link.')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-white/50 text-xs border-b border-white/5">
            <th className="text-start p-3 font-medium">{t('المزود', 'Provider')}</th>
            <th className="text-start p-3 font-medium">{t('المنصة', 'Platform')}</th>
            <th className="text-start p-3 font-medium">{t('العميل', 'Customer')}</th>
            <th className="text-start p-3 font-medium">{t('المبلغ', 'Amount')}</th>
            <th className="text-start p-3 font-medium">{t('الحالة', 'Status')}</th>
            <th className="text-start p-3 font-medium">{t('التاريخ', 'Created')}</th>
            <th className="text-end p-3 font-medium">{t('إجراءات', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {links.map((l) => {
            const provMeta = providerMeta[l.provider] || {};
            const platMeta = PLATFORM_META[l.platform] || {};
            const stMeta = STATUS_META[l.status] || STATUS_META.pending;
            const SIcon = stMeta.icon;
            const provName = isAr ? provMeta.display_name_ar || l.provider : provMeta.display_name || l.provider;
            return (
              <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`payment-link-row-${l.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-md flex items-center justify-center overflow-hidden bg-white/5"
                          style={{ border: `1px solid ${provMeta.color || '#666'}30` }}>
                      {provMeta.logo_url ? (
                        <img src={provMeta.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <span className="text-[10px] font-bold" style={{ color: provMeta.color || '#666' }}>
                          {(provName || '?')[0]}
                        </span>
                      )}
                    </span>
                    <span className="text-white/90">{provName}</span>
                  </div>
                </td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${platMeta.color}15`, color: platMeta.color, border: `1px solid ${platMeta.color}30` }}>
                    {isAr ? platMeta.ar : platMeta.en}
                  </span>
                </td>
                <td className="p-3">
                  <div className="text-white/90">{l.customer_name}</div>
                  {l.customer_email && <div className="text-white/40 text-xs">{l.customer_email}</div>}
                </td>
                <td className="p-3 text-white/90 font-mono">
                  {floorAED(l.amount).toFixed(2)} <span className="text-white/40 text-xs">{l.currency}</span>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${stMeta.color}`}>
                    <SIcon className="w-3 h-3" />
                    {isAr ? stMeta.ar : stMeta.en}
                  </span>
                </td>
                <td className="p-3 text-white/60 text-xs">
                  {new Date(l.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB', {
                    year: '2-digit', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
                  })}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => copyLink(l.checkout_url)}
                            className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                            title={t('نسخ الرابط', 'Copy link')}
                            data-testid={`payment-link-copy-${l.id}`}>
                      <Copy className="w-4 h-4" />
                    </button>
                    <a href={l.checkout_url} target="_blank" rel="noreferrer"
                       className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                       title={t('فتح', 'Open')}
                       data-testid={`payment-link-open-${l.id}`}>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={() => deleteLink(l.id)}
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400/60 hover:text-red-400"
                            title={t('حذف', 'Delete')}
                            data-testid={`payment-link-delete-${l.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
